import { useMemo } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { useSupportedBookingSoftware } from '@frontend/api-ai-settings';
import { CustomizationFlagTypes } from '@frontend/api-customization-flags';
import { hasSchemaACL, isWeaveUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { useHasPhoneSettingsAccess, usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useMessagingAIRCustomizationFlag } from '@frontend/shared';
import { shell } from '@frontend/shell-utils';
import { useWeaveAssistantSettingsFeatureFlag } from '@frontend/weave-assistant-hooks';
import { SettingsRoutes } from './settings-routes';
import type { RouteParams } from './types';

type LabelTypeFunction<K extends SettingsRoutes> = (params: RouteParams<K>) => string | Promise<string>;

type DynamicRoutes = {
  [K in SettingsRoutes]: {
    label: LabelTypeFunction<K> | string;
    description?: string;
    aliases?: string[];
    show?: boolean;
  };
};

export type AllPathsMap = Partial<DynamicRoutes>;

export function useSettingsPathLabelMap() {
  const { t } = useTranslation('settingsNav');
  const { getLocationName, selectedLocationIds, accessibleLocationData, selectedOrgId } = useAppScopeStore();
  const { getFeatureFlagValue, getCustomizationFlag } = useScopedAppFlagStore();
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');

  const selectedLocations = useMemo(
    () =>
      settingsTenantLocation?.locationType === 'databeta'
        ? [accessibleLocationData[settingsTenantLocation.id]]
        : selectedLocationIds?.map((id) => accessibleLocationData[id]).filter(Boolean) ?? [],
    [settingsTenantLocation, accessibleLocationData, selectedLocationIds]
  );

  const phoneAccess = useHasPhoneSettingsAccess({
    selectedLocations,
  });

  const hasAiReceptionistACL = selectedLocationIds.some((locationId) =>
    hasSchemaACL(locationId, Permission.AI_SETTINGS_WRITE)
  );

  const hasBridgelineSettingsAccess = getFeatureFlagValue('bridgeline-settings');

  const { hasSupportedBookingSoftware } = useSupportedBookingSoftware({
    locationId: selectedLocationIds[0],
    orgId: selectedOrgId,
    isEnabled: hasBridgelineSettingsAccess && hasAiReceptionistACL,
  });

  const hasWeaveAssistantSettings = useWeaveAssistantSettingsFeatureFlag();
  const { isActive: hasAIReceptionistAccess } = useMessagingAIRCustomizationFlag(selectedLocationIds);

  // memoized because lots of calls to i18n t() add up
  const pathLabelMap: AllPathsMap = useMemo(() => {
    const hasShellThemeEnabled = shell.isShell && shell.featureAvailability?.has('shell-theme');
    const showAppCustomizations = !shell.isShell || hasShellThemeEnabled;

    const hasDomainVerifyAccess = getFeatureFlagValue('domain-verify-settings');

    const hasTenDLCaccess = getFeatureFlagValue('messaging-compliance-settings');

    const hasImageGalleryAccess = getFeatureFlagValue('image-gallery-in-settings');

    const hasFaxEmailUpdates = getFeatureFlagValue('fax-settings-email-updates');

    const hasScheduleDefinitionAccess = getFeatureFlagValue('schedule-definitions', 'ALL');

    const hasAiSettingsAccess = hasBridgelineSettingsAccess && hasAiReceptionistACL && hasSupportedBookingSoftware;

    const hasMessageQueueAccess =
      selectedLocationIds.filter((locationId) => hasSchemaACL(locationId, Permission.MESSAGE_QUEUE_MANAGE)).length > 0;
    // TODO: BE needs to add the respective permission to the right roles in the meantime before we roll this back out
    // const hasAutoMessagingAccess =
    //   selectedLocationIds.filter((locationId) => hasSchemaACL(locationId, Permission.AUTO_MESSAGING_MANAGE)).length > 0;
    const canViewRecordHistory =
      selectedLocationIds.filter((locationId) => hasSchemaACL(locationId, Permission.VIEW_FULL_PERSON_DATA)).length > 0;

    const canViewTextConnect = getCustomizationFlag('textconnect').every(
      (flag) =>
        flag?.state === CustomizationFlagTypes.CustomizationFlagState.ACTIVE ||
        flag?.state === CustomizationFlagTypes.CustomizationFlagState.PROMOTE
    );

    const pathLabelMapMemoized: AllPathsMap = {
      '/organization': {
        label: t('Organization Settings') /* AUTO-GENERATED */,
        description: "User can manage the organization's settings from this page." /* AUTO-GENERATED */,
      },
      '/organization/users': {
        label: t('Organization Users') /* AUTO-GENERATED */,
        description: "User can manage the organization's users from this page." /* AUTO-GENERATED */,
      },
      '/messaging': {
        label: t('Messaging') /* AUTO-GENERATED */,
        description:
          'Primary messaging module overview. May contain shortcuts to inbox, archived messages, and templates.' /* AUTO-GENERATED */,
      },
      '/personal': {
        label: t('Personal Settings') /* AUTO-GENERATED */,
        description: 'User can manage their personal settings from this page.' /* AUTO-GENERATED */,
      },
      '/personal/phone/call-pop': {
        label: t('Call Pop') /* AUTO-GENERATED */,
        description:
          'User can configure their personal call pop settings for incoming calls from this page.' /* AUTO-GENERATED */,
        show: phoneAccess.hasPhonesCustomizationFlag,
      },
      '/personal/phone/device-connection': {
        label: t('Device Connection') /* AUTO-GENERATED */,
        description: 'Configure your personal phone device connection settings and preferences.' /* AUTO-GENERATED */,
        show: phoneAccess.hasPhonesCustomizationFlag,
      },
      '/personal/phone/missed-call': {
        label: t('Missed Call') /* AUTO-GENERATED */,
        description:
          'User can change their personal settings for missed calls, likely including missed call notifications or automated responses, from this page.' /* AUTO-GENERATED */,
        show: phoneAccess.hasPhonesCustomizationFlag,
      },
      '/tags': {
        label: t('Tags') /* AUTO-GENERATED */,
        description:
          'User can manage the tags from this link, which likely includes creating, editing, or deleting tags used for categorizing contacts or communications.' /* AUTO-GENERATED */,
      },
      '/tags/create': {
        label: t('New Tag') /* AUTO-GENERATED */,
        description: 'User can create new tags from this page within the settings section.' /* AUTO-GENERATED */,
      },
      '/tags/edit/:tagId': {
        label: t('Edit Tag') /* AUTO-GENERATED */,
      },
      '/tags/duplicate/:tagId': {
        label: t('Duplicate Tag') /* AUTO-GENERATED */,
      },
      '/tags/quick-create': {
        label: t('New Tag') /* AUTO-GENERATED */,
        description: 'User can quickly create new tags from this link within the settings.' /* AUTO-GENERATED */,
      },
      '/digital-forms': {
        label: t('Forms') /* AUTO-GENERATED */,
        description:
          'Shortcut to the digital forms module. View, create, or manage practice forms in a centralized place.' /* AUTO-GENERATED */,
      },
      '/payments': {
        label: t('Payments') /* AUTO-GENERATED */,
        description:
          'Main payments overview page. Access invoices, statements, settings, and payment-related features.' /* AUTO-GENERATED */,
      },
      '/payments/general-settings': {
        label: t('General Settings') /* AUTO-GENERATED */,
        description: 'User can manage the general settings related to payments from this page.' /* AUTO-GENERATED */,
      },
      '/payments/online-bill-pay': {
        label: t('Online Bill Pay') /* AUTO-GENERATED */,
        description: 'User can manage the settings for online bill payments from this page.' /* AUTO-GENERATED */,
      },
      '/payments/payment-methods': {
        label: t('Payment Methods') /* AUTO-GENERATED */,
        description: 'User can manage payment methods from this page.' /* AUTO-GENERATED */,
      },
      '/payments/public-business-information': {
        label: t('Public Business Information') /* AUTO-GENERATED */,
      },
      '/phone': {
        label: t('Phone') /* AUTO-GENERATED */,
      },
      '/phone/audio-library': {
        label: t('Audio Library') /* AUTO-GENERATED */,
        description:
          'User can manage the audio files and sound bites for the phone system from this page.' /* AUTO-GENERATED */,
        show: phoneAccess.hasAudioLibraryAccess,
      },
      '/phone/call-blocking': {
        label: t('Call Blocking') /* AUTO-GENERATED */,
        description: 'User can manage call blocking settings from this page.' /* AUTO-GENERATED */,
        show: phoneAccess.hasCallBlockingAccess,
      },
      '/phone/call-groups': {
        label: t('Call Groups') /* AUTO-GENERATED */,
        description:
          'User can manage phone call groups from this link, including creating, editing, or deleting groups and assigning members.' /* AUTO-GENERATED */,
        show: phoneAccess.hasCallGroupsAccess,
      },
      '/phone/call-queues': {
        label: t('Call Queues') /* AUTO-GENERATED */,
        description: 'User can manage call queues settings for the phone system from this page.' /* AUTO-GENERATED */,
        show: phoneAccess.hasCallQueuesAccess,
      },
      '/phone/call-queues/:id': {
        label: t('Call Queue') /* AUTO-GENERATED */,
        show: phoneAccess.hasCallQueuesAccess,
      },
      '/phone/call-recording': {
        label: t('Call Recording') /* AUTO-GENERATED */,
        description:
          'User can manage the call recording settings for the phone system from this page.' /* AUTO-GENERATED */,
        show: phoneAccess.hasCallRecordingAccess,
      },
      '/phone/call-routes': {
        label: t('Call Routes') /* AUTO-GENERATED */,
        description:
          'User can manage and configure call routes for their phone system from this page.' /* AUTO-GENERATED */,
        show: phoneAccess.hasCallRoutesAccess,
      },
      '/phone/call-routes/:id': {
        label: t('Call Route') /* AUTO-GENERATED */,
        show: phoneAccess.hasCallRouteDetailsAccess,
      },
      '/phone/contact-directories': {
        label: t('Contact Directories') /* AUTO-GENERATED */,
        description: 'User can manage or configure phone contact directories from this page.' /* AUTO-GENERATED */,
        show: phoneAccess.hasContactDirectoriesAccess,
      },
      '/phone/departments': {
        label: t('Departments') /* AUTO-GENERATED */,
        description:
          'User can manage phone system departments from this link, including adding, editing, or viewing department settings.' /* AUTO-GENERATED */,
        show: phoneAccess.hasDepartmentsAccess,
      },
      '/phone/departments/:id': {
        label: t('Department') /* AUTO-GENERATED */,
        show: phoneAccess.hasDepartmentsAccess,
      },
      '/phone/devices': {
        label: t('Device Settings') /* AUTO-GENERATED */,
        show: phoneAccess.hasDeviceSettingsAccess,
      },
      '/phone/line-keys': {
        label: t('Line Keys') /* AUTO-GENERATED */,
        description:
          'User can manage or configure the line keys for their phone system from this page.' /* AUTO-GENERATED */,
        show: phoneAccess.hasLineKeysAccess,
      },
      '/phone/mainline': {
        label: t('Mainline') /* AUTO-GENERATED */,
        description: 'User can manage the settings for the main phone line from this page.' /* AUTO-GENERATED */,
        show: phoneAccess.hasMainLineAccess,
      },
      '/phone/office-hours': {
        label: t('Office-Hours') /* AUTO-GENERATED */,
        description:
          'User can manage the office hours settings for the phone system from this page.' /* AUTO-GENERATED */,
        show: phoneAccess.hasOfficeHoursAccess,
      },
      '/phone/overrides': {
        label: t('Overrides') /* AUTO-GENERATED */,
        description:
          'User can manage specific phone system overrides or exceptions from this link, such as special routing rules or temporary changes to phone behavior.' /* AUTO-GENERATED */,
        show: phoneAccess.hasOverridesAccess,
      },
      '/phone/phone-hours': {
        label: t('Phone Hours') /* AUTO-GENERATED */,
        description: 'User can manage the phone operating hours from this page.' /* AUTO-GENERATED */,
        aliases: ['Office Hours'],
        show: phoneAccess.hasOfficeHoursAccess,
      },
      '/phone/phone-tree-quick-actions/:id': {
        label: t('Phone Tree') /* AUTO-GENERATED */,
        show: phoneAccess.hasPhoneTreeQuickActionsAccess,
      },
      '/phone/phone-tree-quick-actions': {
        label: t('Phone Tree Quick Actions') /* AUTO-GENERATED */,
        description:
          'User can manage or configure quick actions for the phone tree (IVR) system from this page.' /* AUTO-GENERATED */,
        show: phoneAccess.hasPhoneTreeQuickActionsAccess,
      },
      '/phone/voicemail-box': {
        label: t('Voicemail Boxes') /* AUTO-GENERATED */,
        description: 'User can manage their phone voicemail boxes from this page.' /* AUTO-GENERATED */,
        show: phoneAccess.hasVoicemailBoxesAccess,
      },
      '/phone/voicemail-box/:id': {
        label: t('Voicemail Box') /* AUTO-GENERATED */,
        show: phoneAccess.hasVoicemailBoxesAccess,
      },
      '/phone/hold-music': {
        label: t('Hold Music') /* AUTO-GENERATED */,
        description:
          'User can manage the hold music from this link which includes changing the music' /* AUTO-GENERATED */,
        show: phoneAccess.hasHoldMusicAccess,
      },
      '/organization/phone-numbers': {
        label: t('Phone Numbers and Porting') /* AUTO-GENERATED */,
      },
      '/organization/phone-numbers/port-request': {
        label: t('Submit Port Request') /* AUTO-GENERATED */,
      },
      '/messaging/templates': {
        label: t('Templates') /* AUTO-GENERATED */,
        description:
          'User can manage the messaging templates from this link, including creating new ones or editing existing ones.' /* AUTO-GENERATED */,
      },
      '/messaging/templates/create': {
        label: t('New Template') /* AUTO-GENERATED */,
      },
      '/messaging/templates/duplicate/:templateId': {
        label: t('Duplicate Template') /* AUTO-GENERATED */,
      },
      '/messaging/templates/edit/:templateId': {
        label: t('Edit Template') /* AUTO-GENERATED */,
      },
      '/messaging/templates/inbox-edit/:templateId': {
        label: t('Edit Manual Template') /* AUTO-GENERATED */,
      },
      '/messaging/templates/import': {
        label: t('Import Templates') /* AUTO-GENERATED */,
      },
      '/payments/terminals': {
        label: t('Terminal Settings') /* AUTO-GENERATED */,
      },
      '/email': {
        label: t('Email') /* AUTO-GENERATED */,
        description:
          'Email marketing settings. Manage email addresses and domains used with email marketing.' /* AUTO-GENERATED */,
        show: hasDomainVerifyAccess,
      },
      '/fax': {
        label: t('Fax') /* AUTO-GENERATED */,
        description:
          'Primary fax module overview, providing quick access to inbox, drafts, and fax configuration.' /* AUTO-GENERATED */,
      },
      '/reviews': {
        label: t('Social') /* AUTO-GENERATED */,
        description: 'User can manage settings related to reviews from this page.' /* AUTO-GENERATED */,
      },
      '/organization/billing-information': {
        label: t('Billing Information') /* AUTO-GENERATED */,
        description:
          "User can manage or view their organization's billing information from this page." /* AUTO-GENERATED */,
      },
      '/organization/invoice-history': {
        label: t('Invoice History') /* AUTO-GENERATED */,
        description: "User can view the organization's invoice history from this page." /* AUTO-GENERATED */,
      },
      '/organization/single-sign-on': {
        label: t('Single Sign On') /* AUTO-GENERATED */,
        description:
          'User can configure or manage the Single Sign-On (SSO) settings for their organization from this page.' /* AUTO-GENERATED */,
      },
      '/organization/payment-methods': {
        label: t('Payment Methods') /* AUTO-GENERATED */,
        description:
          "Users can configure the payment methods that will be used to charge the weave's monthly/annual subscription charges." /* AUTO-GENERATED */,
      },
      '/organization/invoice-history/:locationId': {
        label: (params) => getLocationName(params.locationId) || params.locationId,
      },
      '/personal/diagnostics': {
        label: t('Device & App Diagnostics') /* AUTO-GENERATED */,
        description:
          'User can self diagnose any issues related to the devices and apps.They can check any related System health alerts.' /* AUTO-GENERATED */,
      },
      '/messaging/mark-confirmation-as-read': {
        label: t('Mark Confirmed') /* AUTO-GENERATED */,
        description:
          'User can manage settings related to how messaging confirmations are marked as read.' /* AUTO-GENERATED */,
        show: phoneAccess.hasPhonesCustomizationFlag,
      },
      '/schedule': {
        label: t('Schedule') /* AUTO-GENERATED */,
        description: 'Primary scheduling hub, linking to calendar, appointment' /* AUTO-GENERATED */,
      },
      '/schedule/appointment-types': {
        label: t('Appointment Types') /* AUTO-GENERATED */,
        description:
          'Create and manage different types of appointments with specific durations and requirements.' /* AUTO-GENERATED */,
      },
      '/schedule/message-templates': {
        label: t('Message Templates') /* AUTO-GENERATED */,
        description:
          'Create and manage message templates for appointment confirmations, reminders, and communications.' /* AUTO-GENERATED */,
      },
      '/schedule/appointment-hours': {
        label: t('Appointment Hours') /* AUTO-GENERATED */,
        description:
          'Configure available appointment hours and scheduling windows for your practice.' /* AUTO-GENERATED */,
      },
      '/schedule/message-templates/appointment-confirmed/:locationId': {
        label: t('Appointment Confirmed') /* AUTO-GENERATED */,
      },
      '/schedule/message-templates/request-received/:locationId': {
        label: t('Request Received') /* AUTO-GENERATED */,
      },
      '/schedule/custom-fields': {
        label: t('Custom Fields') /* AUTO-GENERATED */,
        description:
          'Create and manage custom fields for capturing additional information during appointment booking.' /* AUTO-GENERATED */,
      },
      '/personal/customize-app': {
        label: t('Customize Your App') /* AUTO-GENERATED */,
        description: 'Personalize your app appearance, theme, and user interface preferences.' /* AUTO-GENERATED */,
        show: !!showAppCustomizations,
      },
      '/messaging/auto-message-inbox-visibility': {
        label: t('Auto-Message Inbox Visibility') /* AUTO-GENERATED */,
        description:
          'Control which automated messages are visible in the main inbox versus processed silently.' /* AUTO-GENERATED */,
        show: phoneAccess.hasPhonesCustomizationFlag,
      },
      '/subscriptions': {
        label: t('Subscriptions') /* AUTO-GENERATED */,
        description:
          'View and manage your current subscriptions, billing, and feature access levels.' /* AUTO-GENERATED */,
      },
      '/subscriptions/feature/:id': {
        label: t('Feature Subscriptions') /* AUTO-GENERATED */,
      },
      '/subscriptions/manage-subscription': {
        label: t('Manage Subscription') /* AUTO-GENERATED */,
        description:
          'Modify your subscription plan, add features, and manage billing preferences.' /* AUTO-GENERATED */,
      },
      '/schedule/appointment-types/:locationId': {
        label: (params) => getLocationName(params.locationId) || params.locationId,
      },
      '/schedule/manage-providers/:locationId': {
        label: (params) => getLocationName(params.locationId) || params.locationId,
      },
      '/schedule/schedule-definition-templates': {
        label: t('Schedule Definitions (Templates)') /* AUTO-GENERATED */,
        description:
          'Manage your treatment rooms and chairs for optimal scheduling and operations.' /* AUTO-GENERATED */,
      },
      '/schedule/schedule-definition-templates/:locationId': {
        label: (params) => getLocationName(params.locationId) || params.locationId,
      },
      '/schedule/manage-providers': {
        label: t('Manage Providers') /* AUTO-GENERATED */,
        description:
          'Add, edit, and manage healthcare providers and their scheduling availability.' /* AUTO-GENERATED */,
      },
      '/insurance': {
        label: t('Insurance') /* AUTO-GENERATED */,
        description: 'Manage insurance verification settings and payer connection configurations.' /* AUTO-GENERATED */,
      },
      '/insurance/automatic-verification': {
        label: t('Automatic Verification') /* AUTO-GENERATED */,
        description:
          'Configure automatic insurance verification settings and eligibility checking.' /* AUTO-GENERATED */,
      },
      '/insurance/automatic-verification/:locationId': {
        label: (params) => getLocationName(params.locationId) || params.locationId,
      },

      '/personal/notifications/:type': {
        label: (params) => {
          const map: Record<string, string> = {
            'chat-message-new': t('Chat'),
            'fax-new': t('Fax'),
            'form-submission-new': t('Forms'),
            'review-new': t('Reviews'),
            'sms-message-new': t('SMS Messages'),
            payments: t('Payments'),
            'schedule-request-new': t('Schedule Request'),
            'voicemail-message': t('Voicemail'),
            'unhappy-call': t('Call Intelligence Notifications'),
          };
          return map[params.type];
        },
      },
      '/personal/notifications/tag-notifications': {
        label: t('Tags') /* AUTO-GENERATED */,
        description:
          'User can manage and configure their personal notifications for specific tags from this page.' /* AUTO-GENERATED */,
      },
      '/personal/notifications/position-and-dismissal': {
        label: t('Position and Dismissal') /* AUTO-GENERATED */,
        description:
          'Configure notification preferences for desktop notification positioning and auto-dismissal.' /* AUTO-GENERATED */,
      },
      '/messaging/show-unreplied-message-status': {
        label: t('Unreplied Message Status Visiblity') /* AUTO-GENERATED */,
        description:
          'Configure visibility settings for unreplied message status indicators in the inbox.' /* AUTO-GENERATED */,
      },
      '/personal/preferences/accessibility': {
        label: t('Accessibility') /* AUTO-GENERATED */,
        description:
          'Configure accessibility settings including font size, contrast, and assistive features.' /* AUTO-GENERATED */,
      },
      '/payments/account-info': {
        label: t('Account Info and Status') /* AUTO-GENERATED */,
        description:
          'View payment processor account information, status, and configuration details.' /* AUTO-GENERATED */,
      },
      '/messaging/auto-messages': {
        label: t('Auto-Messages') /* AUTO-GENERATED */,
        description:
          'Create and manage automated messaging rules for appointment reminders and follow-ups.' /* AUTO-GENERATED */,
        // show: hasAutoMessagingAccess,
      },
      '/messaging/auto-messages/:ruleId': {
        label: t('Auto-Messages Rule') /* AUTO-GENERATED */,
        // show: hasAutoMessagingAccess,
      },
      '/messaging/auto-messages/:ruleId/composer': {
        label: t('Auto-Messages Composer') /* AUTO-GENERATED */,
      },
      '/messaging/auto-messages/:ruleId/editor': {
        label: t('Auto-Messages Editor') /* AUTO-GENERATED */,
      },
      '/messaging/auto-messages/:ruleId/new-email': {
        label: t('Auto-Messages Create Email') /* AUTO-GENERATED */,
      },
      '/messaging/auto-messages/:ruleId/templates': {
        label: t('Auto-Messages Templates') /* AUTO-GENERATED */,
      },
      '/image-gallery': {
        label: t('Image Gallery') /* AUTO-GENERATED */,
        description:
          'Manage practice images and media assets used across various communications and forms.' /* AUTO-GENERATED */,
        show: hasImageGalleryAccess,
      },
      '/organization/brand': {
        label: t('Brand Details') /* AUTO-GENERATED */,
        description:
          "Manage your practice's brand information including logos, colors, and visual identity." /* AUTO-GENERATED */,
      },

      '/schedule/manage-providers/edit/:providerId': {
        label: t('Edit Provider') /* AUTO-GENERATED */,
      },
      '/schedule/manage-providers/:locationId/edit/:providerId': {
        label: t('Edit Provider') /* AUTO-GENERATED */,
      },
      '/schedule/booking-site-setup': {
        label: t('Booking Site Setup') /* AUTO-GENERATED */,
        description:
          'Configure your online booking site appearance, settings, and patient self-scheduling options.' /* AUTO-GENERATED */,
      },
      '/organization/messaging-compliance-registration': {
        label: t('Messaging Compliance Registration') /* AUTO-GENERATED */,
        description:
          'Complete messaging compliance registration for 10DLC and enhanced SMS delivery.' /* AUTO-GENERATED */,
        show: hasTenDLCaccess,
      },
      '/payments/justifi-settings': {
        label: t('JustiFi Setup') /* AUTO-GENERATED */,
        description:
          'Configure JustiFi payment processing integration and merchant account settings.' /* AUTO-GENERATED */,
      },
      '/organization/business-verification': {
        label: t('Business Verification') /* AUTO-GENERATED */,
        description:
          'Complete business verification process for enhanced features and compliance requirements.' /* AUTO-GENERATED */,
      },
      '/messaging/message-queue': {
        label: t('Message Queue') /* AUTO-GENERATED */,
        description:
          'Monitor and manage the message delivery queue, including pending and failed messages.' /* AUTO-GENERATED */,
        show: hasMessageQueueAccess,
      },
      '/fax/history': {
        label: t('Fax History') /* AUTO-GENERATED */,
        description:
          'View historical records of sent and received faxes with delivery status and timestamps.' /* AUTO-GENERATED */,
      },
      '/messaging/record-history': {
        label: t('Record History') /* AUTO-GENERATED */,
        description:
          'View comprehensive message history and communication records for patients and contacts.' /* AUTO-GENERATED */,
        show: canViewRecordHistory,
      },
      '/messaging/record-history/:type/:recordId': {
        label: t('Record Details') /* AUTO-GENERATED */,
        show: canViewRecordHistory,
      },
      '/messaging/text-connect': {
        label: t('Text Connect Settings') /* AUTO-GENERATED */,
        description:
          'Configure Text Connect integration settings for enhanced messaging capabilities.' /* AUTO-GENERATED */,
        show: canViewTextConnect,
      },
      '/messaging/text-connect/:locationId': {
        label: (params) => getLocationName(params.locationId),
        show: canViewTextConnect,
      },
      '/organization/data-sync': {
        label: t('Data Sync - Choose Location') /* AUTO-GENERATED */,
        description:
          'Configure data synchronization settings and choose locations for practice management integration.' /* AUTO-GENERATED */,
      },
      '/organization/csv-upload': {
        label: t('Imported Contact Lists') /* AUTO-GENERATED */,
        description:
          'View and manage contact lists that have been imported from CSV files or other sources.' /* AUTO-GENERATED */,
      },
      '/organization/csv-upload/map': {
        label: t('CSV Mapping') /* AUTO-GENERATED */,
      },
      '/organization/csv-upload/preview': {
        label: t('CSV Preview') /* AUTO-GENERATED */,
      },
      '/organization/data-sync/location/:locationId': {
        label: t('Data Sync') /* AUTO-GENERATED */,
      },
      '/organization/data-sync/location/:locationId/source/:sourceId': {
        label: t('Customize Integration') /* AUTO-GENERATED */,
      },
      '/organization/data-sync/location/:locationId/new': {
        label: t('Add New Integration') /* AUTO-GENERATED */,
      },
      '/payments/sunbit': {
        label: t('Sunbit') /* AUTO-GENERATED */,
        description:
          'Configure Sunbit financing integration for patient payment options and financing solutions.' /* AUTO-GENERATED */,
      },
      '/organization/users/setup-mobile-number/:nonce': {
        label: t('Mobile Number Verification') /* AUTO-GENERATED */,
      },
      '/messaging/actionable-tab': {
        label: t('Utilize Actionable Tab') /* AUTO-GENERATED */,
        description:
          'Configure the actionable tab feature for processing and organizing incoming messages.' /* AUTO-GENERATED */,
      },
      '/messaging/appointment-confirmations': {
        label: t('Show Appointment Confirmations') /* AUTO-GENERATED */,
        description:
          'Configure how appointment confirmation messages are displayed and processed in the inbox.' /* AUTO-GENERATED */,
      },
      '/messaging/auto-messages-processing': {
        label: t('Auto-Messages Processing') /* AUTO-GENERATED */,
        description: 'Configure how automated messages are processed and handled by the system.' /* AUTO-GENERATED */,
      },
      '/messaging/bulk-messages-processing': {
        label: t('Bulk Text Messages Processing') /* AUTO-GENERATED */,
        description:
          'Configure how bulk text message campaigns are processed and handled by the system.' /* AUTO-GENERATED */,
      },
      '/personal/email/fax': {
        label: t('Fax') /* AUTO-GENERATED */,
        description:
          'Configure personal email notification preferences for fax-related communications.' /* AUTO-GENERATED */,
        show: hasFaxEmailUpdates,
      },
      '/personal/notifications/sms-messages': {
        label: t('SMS Message Notification Preferences') /* AUTO-GENERATED */,
        description:
          'Configure personal notification preferences for SMS message alerts and updates.' /* AUTO-GENERATED */,
      },
      '/personal/notifications/reputation': {
        label: t('Reviews Notification Preferences') /* AUTO-GENERATED */,
        description:
          'Configure personal notification preferences for new reviews and reputation updates.' /* AUTO-GENERATED */,
      },
      '/organization/csv-upload/file-mapping': {
        label: t('File Mapping') /* AUTO-GENERATED */,
      },
      '/organization/csv-upload/review-upload': {
        label: t('Review Your Upload') /* AUTO-GENERATED */,
      },
      '/weave-assistant': {
        label: t('Weave Assistant') /* AUTO-GENERATED */,
        description: 'User can manage the settings for the Weave Assistant from this page.' /* AUTO-GENERATED */,
        show: hasWeaveAssistantSettings /* AUTO-GENERATED */,
      },
      '/weave-assistant/assist-with-calls': {
        label: t('Assist With Calls') /* AUTO-GENERATED */,
        description:
          'Configure Weave Assistant to help with call handling, routing, and customer service tasks.' /* AUTO-GENERATED */,
        show: hasWeaveAssistantSettings,
      },
      '/weave-assistant/assist-with-text-messages': {
        label: t('Assist With Text Messages') /* AUTO-GENERATED */,
        description:
          'Configure Weave Assistant to help with text message responses and patient communications.' /* AUTO-GENERATED */,
        show: hasWeaveAssistantSettings,
      },
      '/weave-assistant/general-details': {
        label: t('General Details') /* AUTO-GENERATED */,
        description:
          'Configure general practice information and details for Weave Assistant knowledge base.' /* AUTO-GENERATED */,
        show: hasWeaveAssistantSettings,
      },
      '/weave-assistant/office-amenities': {
        label: t('Office Amenities') /* AUTO-GENERATED */,
        description:
          'Configure office amenities and facility information for Weave Assistant to share with patients.' /* AUTO-GENERATED */,
        show: hasWeaveAssistantSettings,
      },
      '/weave-assistant/payment-and-insurance': {
        label: t('Payment & Insurance') /* AUTO-GENERATED */,
        description:
          'Configure payment and insurance information for Weave Assistant to help patients with billing inquiries.' /* AUTO-GENERATED */,
        show: hasWeaveAssistantSettings,
      },
      '/weave-assistant/schedule-and-availability': {
        label: t('Schedule & Availability') /* AUTO-GENERATED */,
        description:
          'Configure scheduling and availability information for Weave Assistant to help with appointment booking.' /* AUTO-GENERATED */,
        show: hasWeaveAssistantSettings,
      },
      '/weave-assistant/advanced-training': {
        label: t('Advanced Training') /* AUTO-GENERATED */,
        description:
          'Provide advanced training data and custom instructions to enhance Weave Assistant performance.' /* AUTO-GENERATED */,
        show: hasWeaveAssistantSettings,
      },
      '/phone/contact-directories/:id': {
        label: t('Contact Directories') /* AUTO-GENERATED */,
      },
      '/insurance/payer-connection-management': {
        label: t('Payer Connection Management') /* AUTO-GENERATED */,
        description:
          'Manage connections to insurance payers and configure eligibility verification settings.' /* AUTO-GENERATED */,
      },
      '/personal/notifications/ai-receptionist': {
        label: t('AI Receptionist') /* AUTO-GENERATED */,
        show: hasAIReceptionistAccess /* AUTO-GENERATED */,
      },
      '/insurance/payer-connection-management/:locationId': {
        label: (params) => getLocationName(params.locationId) || params.locationId,
      },
      '/personal/version': {
        label: t('Version and Channel') /* AUTO-GENERATED */,
        description:
          'View current app version information and configure update channel preferences.' /* AUTO-GENERATED */,
      },
      '/organization/ip-restrictions': {
        label: t('IP Restrictions') /* AUTO-GENERATED */,
        description:
          'Configure IP address restrictions to enhance security and control system access.' /* AUTO-GENERATED */,
      },
      '/analytics': {
        label: t('Analytics') /* AUTO-GENERATED */,
        description:
          'High-level analytics dashboard providing snapshots of calls, messages, appointments, and other KPIs.' /* AUTO-GENERATED */,
      },
      '/schedule/operatories': {
        label: t('Operatories') /* AUTO-GENERATED */,
        description:
          'Configure operatory rooms and treatment spaces for appointment scheduling and resource management.' /* AUTO-GENERATED */,
      },
      '/weave-assistant/weave-assistant': {
        label: t('Weave Assistant') /* AUTO-GENERATED */,
        description:
          'Main Weave Assistant configuration and overview of AI capabilities and training status.' /* AUTO-GENERATED */,
      },
      '/schedule/operatories/:locationId': {
        label: (params) => getLocationName(params.locationId) || params.locationId,
      },
      '/schedule/operatories/edit/:operatoryId': {
        label: t('Edit Operatory') /* AUTO-GENERATED */,
      },
      '/schedule/operatories/:locationId/edit/:operatoryId': {
        label: t('Edit Operatory') /* AUTO-GENERATED */,
      },
      '/brand': {
        label: t('Brand') /* AUTO-GENERATED */,
        description:
          'User can manage or configure branding settings for their Weave account or services from this page.' /* AUTO-GENERATED */,
      },
      '/phone/main': {
        label: t('Phone') /* AUTO-GENERATED */,
        description:
          'Configure main phone system settings including general telephony preferences.' /* AUTO-GENERATED */,
      },
      '/ai-configuration': {
        label: t('AI Settings') /* AUTO-GENERATED */,
        description: 'Manage and configure your AI-powered tools and features.' /* AUTO-GENERATED */,
      },
      '/insurance/cdt-code-management': {
        label: t('CDT Code Management') /* AUTO-GENERATED */,
        description: 'Manage your CDT code settings for faster access in Insurance Reports.' /* AUTO-GENERATED */,
      },
      '/insurance/cdt-code-management/:locationId': {
        label: (params) => getLocationName(params.locationId) || params.locationId,
      },
      '/messaging/auto-messages/:ruleId/reviews': {
        label: t('Auto-Messages Reviews Landing Create Page') /* AUTO-GENERATED */,
      },
      '/messaging/auto-messages/:ruleId/reviews/:landingPageTemplateId': {
        label: t('Auto-Messages Reviews Landing Edit Page') /* AUTO-GENERATED */,
      },
      '/digital-forms/customize-kiosk': {
        label: t('Manage Forms List View For Patients') /* AUTO-GENERATED */,
        description:
          'User can customize the settings and appearance of the digital forms kiosk from this page.' /* AUTO-GENERATED */,
      },
      '/digital-forms/medical-history': {
        label: t('Patient Medical History') /* AUTO-GENERATED */,
        description:
          'User can setup medical conditions here and then use it consistently in the form builder. They are also sent back to PMS.' /* AUTO-GENERATED */,
      },
      '/digital-forms/privacy-policy': {
        label: t('Privacy Policy') /* AUTO-GENERATED */,
        description:
          'User can manage the privacy policy settings related to digital forms from this page.' /* AUTO-GENERATED */,
      },
      '/digital-forms/provider-mapping': {
        label: t('Provider Mapping') /* AUTO-GENERATED */,
        description:
          'User can associate Weave Users with Providers from your PMS/EHR.This allows users to view submitted form responses and manage what is written back to the PMS' /* AUTO-GENERATED */,
      },
      '/digital-forms/terms-and-conditions': {
        label: t('Terms & Conditions') /* AUTO-GENERATED */,
        description:
          'User can Insert terms and conditions that will appear as a clickable link in the footer of all your forms pages.' /* AUTO-GENERATED */,
      },
      '/digital-forms/writebacks': {
        label: t('Writeback Settings') /* AUTO-GENERATED */,
        description:
          'User can configure writeback settings for digital forms, likely managing how submitted form data updates other systems.' /* AUTO-GENERATED */,
      },
      '/personal/email/call-intelligence': {
        label: t('Call Intelligence Updates') /* AUTO-GENERATED */,
        description:
          'User can manage personal email settings for call intelligence features from this page.' /* AUTO-GENERATED */,
      },
      '/ai-configuration/ai-receptionist/ai-receptionist': {
        label: t('AI Receptionist') /* AUTO-GENERATED */,
        description:
          'Main Weave Assistant configuration and overview of AI capabilities and training status.' /* AUTO-GENERATED */,
      },
      '/ai-configuration/ai-receptionist': {
        label: t('AI Receptionist') /* AUTO-GENERATED */,
        description:
          'Configure and train your AI Receptionist to better understand your business and goals.' /* AUTO-GENERATED */,
      },
      '/schedule/services/:locationId': {
        label: (params) => getLocationName(params.locationId) || params.locationId,
        description:
          'Define what services you offer. Visit Availability Rules to control when, where and how they’re available.',
        show: hasScheduleDefinitionAccess || hasAiSettingsAccess,
      },
      '/schedule/services': {
        label: t('Services') /* AUTO-GENERATED */,
        description:
          'Define what services you offer. Visit Availability Rules to control when, where and how they’re available.' /* AUTO-GENERATED */,
        show: hasScheduleDefinitionAccess || hasAiSettingsAccess,
      },
      '/ai-configuration/ai-receptionist/business-preferences': {
        label: t('Business Preferences') /* AUTO-GENERATED */,
        description: 'Let your AI Receptionist get to know your business.' /* AUTO-GENERATED */,
      },
      '/ai-configuration/ai-receptionist/online-booking': {
        label: t('Online Booking') /* AUTO-GENERATED */,
        description: 'Allow patients to book services from your website.' /* AUTO-GENERATED */,
      },
      '/ai-configuration/ai-receptionist/web-to-text': {
        label: t('Web to Text') /* AUTO-GENERATED */,
        description: 'Allow patients to text your practice from your website.' /* AUTO-GENERATED */,
      },
      '/ai-configuration/ai-receptionist/business-preferences/amenities': {
        label: t('Amenities') /* AUTO-GENERATED */,
        description: 'Features and experiences offered to patients.' /* AUTO-GENERATED */,
      },
      '/ai-configuration/ai-receptionist/business-preferences/before-after-appointment': {
        label: t('Before & After Appointment') /* AUTO-GENERATED */,
        description: 'Pre & post procedure instructions.' /* AUTO-GENERATED */,
      },
      '/ai-configuration/ai-receptionist/business-preferences/business-profile': {
        label: t('Business Profile') /* AUTO-GENERATED */,
        description: 'Let your AI Receptionist get to know your business.' /* AUTO-GENERATED */,
      },
      '/ai-configuration/ai-receptionist/business-preferences/hidden-preferences': {
        label: t('Hidden Preferences') /* AUTO-GENERATED */,
        description:
          'Provide additional preferences for scheduling, amenities, and FAQ responses.' /* AUTO-GENERATED */,
        show: isWeaveUser(),
      },
      '/ai-configuration/ai-receptionist/business-preferences/insurance': {
        label: t('Insurance') /* AUTO-GENERATED */,
        description: 'Accepting insurance from patients.' /* AUTO-GENERATED */,
      },
      '/ai-configuration/ai-receptionist/business-preferences/missed-calls': {
        label: t('Missed Calls') /* AUTO-GENERATED */,
        description: 'How should your AI Receptionist respond to missed calls?' /* AUTO-GENERATED */,
      },
      '/ai-configuration/ai-receptionist/business-preferences/notifications': {
        label: t('Notifications') /* AUTO-GENERATED */,
        description: 'Notifications to your practice.' /* AUTO-GENERATED */,
      },
      '/ai-configuration/ai-receptionist/business-preferences/patient-billing': {
        label: t('Patient Billing') /* AUTO-GENERATED */,
        description: 'Accepting payments from patients.' /* AUTO-GENERATED */,
      },
      '/ai-configuration/ai-receptionist/business-preferences/pharmacy-records': {
        label: t('Pharmacy & Records') /* AUTO-GENERATED */,
        description: 'Prescriptions, x-rays, and more.' /* AUTO-GENERATED */,
      },
      '/ai-configuration/ai-receptionist/business-preferences/scheduling': {
        label: t('Scheduling') /* AUTO-GENERATED */,
        description: 'Booking appointments for your practice.' /* AUTO-GENERATED */,
      },
      '/digital-forms/insurance-verification': {
        label: t('Insurance Fields') /* AUTO-GENERATED */,
        description:
          'User can configure automated verification of insurance fields on digital forms.' /* AUTO-GENERATED */,
      },
      '/schedule/availability': {
        label: t('Availability') /* AUTO-GENERATED */,
        description: 'Manange your availability by setting up rules and exceptions' /* AUTO-GENERATED */,
        show: hasScheduleDefinitionAccess || hasAiSettingsAccess,
      },
      '/schedule/availability/:locationId': {
        label: (params) => getLocationName(params.locationId) || params.locationId,
        description: 'Manange your availability by setting up rules and exceptions',
        show: hasScheduleDefinitionAccess || hasAiSettingsAccess,
      },
      '/payments/care-credit': {
        label: t('Account Info and Status') /* AUTO-GENERATED */,
        description:
          'View payment processor account information, status, and configuration details.' /* AUTO-GENERATED */,
      },
    };
    return pathLabelMapMemoized;
  }, [
    getCustomizationFlag,
    getFeatureFlagValue,
    getLocationName,
    hasAIReceptionistAccess,
    hasWeaveAssistantSettings,
    phoneAccess.hasAudioLibraryAccess,
    phoneAccess.hasCallBlockingAccess,
    phoneAccess.hasCallGroupsAccess,
    phoneAccess.hasCallQueuesAccess,
    phoneAccess.hasCallRecordingAccess,
    phoneAccess.hasCallRouteDetailsAccess,
    phoneAccess.hasCallRoutesAccess,
    phoneAccess.hasContactDirectoriesAccess,
    phoneAccess.hasDepartmentsAccess,
    phoneAccess.hasDeviceSettingsAccess,
    phoneAccess.hasHoldMusicAccess,
    phoneAccess.hasLineKeysAccess,
    phoneAccess.hasMainLineAccess,
    phoneAccess.hasOfficeHoursAccess,
    phoneAccess.hasOverridesAccess,
    phoneAccess.hasPhoneTreeQuickActionsAccess,
    phoneAccess.hasPhonesCustomizationFlag,
    phoneAccess.hasVoicemailBoxesAccess,
    hasSupportedBookingSoftware,
    hasAiReceptionistACL,
    hasBridgelineSettingsAccess,
    selectedLocationIds,
    t,
  ]);

  return pathLabelMap;
}
